import { makeAutoObservable } from 'mobx';
import SiteFeatureApi from '../api/siteFeatureApi';
import EnablerApi from '../api/enablerApi';
import {
  getLocalStorageValue,
  setLocalStorageValue,
} from '../utils/localStorage';
import logger from '../logger';
import { siteFeaturesKeys } from '../constants/siteFeaturesKeys';
import {
  AVG_PRICE_BANNER_KEY, ETRN_BANNER_KEY, SAFETY_BANNER_KEY, TRANSPORTER_BANNER_KEY,
} from '../constants/localStorageKeys';

class FeatureStore {
  // Состояния enabler (enablerId -> isAvailable)
  enablersState = new Map();
  isActiveTransporterBannerSiteFeatureActive = false;
  isSafetyBannerSiteFeatureActive = false;
  isEtrnBannerSiteFeatureActive = false;
  isAvgPriceBannerSiteFeatureActive = false;

  constructor() {
    makeAutoObservable(this);
  }

  initializeEnablersAndSiteFeatures = async () => {
    await this.loadSiteFeatures();
    await this.loadBannerEnablersState();
  };

  loadSiteFeatures = async () => {
    try {
      const siteFeaturesIds = [
        siteFeaturesKeys.showActiveTransporterBanner,
        siteFeaturesKeys.showSafetyBanner,
        siteFeaturesKeys.showReadyForEtrnBanner,
        siteFeaturesKeys.showAvgPriceBanner,
      ];
      const query = siteFeaturesIds.map(siteFeatureId => `id=${siteFeatureId}`).join('&');
      const response = await SiteFeatureApi.getSiteFeatures(query);

      if (response.data[siteFeaturesKeys.showActiveTransporterBanner]) {
        this.isActiveTransporterBannerSiteFeatureActive = true;
      }
      if (response.data[siteFeaturesKeys.showSafetyBanner]) {
        this.isSafetyBannerSiteFeatureActive = true;
      }
      if (response.data[siteFeaturesKeys.showReadyForEtrnBanner]) {
        this.isEtrnBannerSiteFeatureActive = true;
      }
      if (response.data[siteFeaturesKeys.showAvgPriceBanner]) {
        this.isAvgPriceBannerSiteFeatureActive = true;
      }
    } catch (error) {
      logger.error(error);
    }
  };

  loadBannerEnablersState = async () => {
    const isAuthorized = window.atiUser && window.atiUser.is_user;
    const enablerIds = [
      TRANSPORTER_BANNER_ENABLER_ID, // eslint-disable-line
      SAFETY_BANNER_ENABLER_ID, // eslint-disable-line
      ETRN_BANNER_ENABLER_ID, // eslint-disable-line
      AVG_PRICE_ENABLER_ID, // eslint-disable-line
    ];

    if (isAuthorized) {
      await this.loadEnablersStateFromAPI(enablerIds);
    } else {
      this.loadEnablersStateFromLocalStorage(enablerIds);
    }
  };

  loadEnablersStateFromAPI = async (enablerIds) => {
    const promises = enablerIds.map(async (enablerId) => {
      try {
        const response = await EnablerApi.getEnablerState(enablerId);
        this.enablersState.set(enablerId, !response.data.is_viewed);
      } catch (error) {
        logger.error(error);
        this.enablersState.set(enablerId, false);
      }
    });
    await Promise.all(promises);
  };

  loadEnablersStateFromLocalStorage = (enablerIds) => {
    const localStorageKeys = {
      [TRANSPORTER_BANNER_ENABLER_ID]: TRANSPORTER_BANNER_KEY, // eslint-disable-line
      [SAFETY_BANNER_ENABLER_ID]: SAFETY_BANNER_KEY, // eslint-disable-line
      [ETRN_BANNER_ENABLER_ID]: ETRN_BANNER_KEY, // eslint-disable-line
      [AVG_PRICE_ENABLER_ID]: AVG_PRICE_BANNER_KEY, // eslint-disable-line
    };

    enablerIds.forEach((enablerId) => {
      const localStorageKey = localStorageKeys[enablerId];
      if (localStorageKey) {
        const isViewed = getLocalStorageValue(localStorageKey);
        this.enablersState.set(enablerId, !isViewed);
      }
    });
  };

  // Проверяет доступность баннера по enablerId
  isBannerAvailable = enablerId => this.enablersState.get(enablerId) ?? true;

  setBannerViewed = async (enablerId, localStorageKey) => {
    this.enablersState.set(enablerId, false);

    setLocalStorageValue(localStorageKey, true);

    if (window.atiUser && window.atiUser.is_user) {
      try {
        await EnablerApi.setEnablerViewed(enablerId);
      } catch (error) {
        logger.error(error);
      }
    }
  };
}

export default FeatureStore;
